// How many document-scan failures an eKYC response body reports, or null when
// it reports none.
//
// The server's attempt counter (validationCount / VALIDATION_COUNT) tallies
// every finalized attempt — successes included — and also rides along in bodies
// that say nothing about a scan outcome (step transitions, liveness session
// setup). Reading it unconditionally made a passing attempt push the counter to
// the Contact-support threshold. So a body contributes only when it reports a
// failure itself.
//
// Two body families, handled differently:
//   - Whole validation records (page load by token, get-next-step) carry a
//     per-step count next to that step's own result, so the count is paired
//     with the outcome of the step it belongs to. A blind search would return
//     whichever step's count came first in key order.
//   - Everything else (check-result, reject-reason, error envelopes) describes
//     a single outcome, so the count may be taken from anywhere in the body
//     once that outcome reads as a failure.
//
// Kept dependency-free so plain Node can import it.

const VC_KEYS = ["validationCount", "VALIDATION_COUNT", "validation_count"];

const FAILED_STEP_RESULT = "Fail";

function findNumber(obj, keys, depth = 0) {
  if (!obj || typeof obj !== "object" || depth >= 5) return null;
  for (const k of Object.keys(obj)) {
    if (keys.includes(k) && typeof obj[k] === "number") return obj[k];
  }
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v && typeof v === "object") {
      const found = findNumber(v, keys, depth + 1);
      if (found != null) return found;
    }
  }
  return null;
}

// The record is the payload itself when loaded by token, and nested under
// `Validation` when it arrives with the next step.
function findValidationRecord(body) {
  if (!body || typeof body !== "object") return null;
  const data = body.data;
  if (!data || typeof data !== "object") return null;
  if (data.Validation && typeof data.Validation === "object") return data.Validation;
  if (data.ComponentResults && typeof data.ComponentResults === "object") return data;
  return null;
}

function indicatesFailedOutcome(body) {
  if (!body || typeof body !== "object") return false;
  if (body.status === "fail" || body.overallStatus === "Fail") return true;
  const data = body.data;
  if (data && typeof data === "object") {
    if (data.status === "fail" || data.overallStatus === "Fail") return true;
    // The reject-reason body is success-wrapped and only ever requested after a
    // failure; its keys are present even when the value is null.
    if ("errorMessageKey" in data || "failReasonAndSolutionList" in data) return true;
  }
  return false;
}

export function docScanFailCount(body) {
  const record = findValidationRecord(body);
  if (record) {
    const stepId = record.CurrentStep && record.CurrentStep.ID;
    const results = record.ComponentResults;
    if (!stepId || !results || typeof results !== "object") return null;
    const step = results[stepId];
    if (!step || typeof step !== "object") return null;
    if (step.Result !== FAILED_STEP_RESULT) return null;
    const count = step.VALIDATION_COUNT;
    return typeof count === "number" && !Number.isNaN(count) ? count : null;
  }
  if (!indicatesFailedOutcome(body)) return null;
  return findNumber(body, VC_KEYS);
}
